import classnames from "classnames";
import { useMemo, useState } from "react";
import { LayoutWrp } from "components";
import heroImage from "assets/images/jumbo-1.png";

const CATEGORY_TAGS = [
  "Education",
  "Technology",
  "Infrastructure",
  "Climate & Environment",
  "Health & Wellness",
  "Poverty & Hunger",
  "Security & Conflict",
  "Justice & Equality",
  "Politics & Governance",
];

const HERO_VARIANTS = [
  {
    id: "current",
    title: "Current layout",
    description:
      "Illustration-led hero with centered message and button. Navigation pills sit just beneath the hero block.",
  },
  {
    id: "compact",
    title: "Compact cards",
    description:
      "Keep all content but squeeze the hero into two cards so the pill navigation feels anchored without extra scroll.",
  },
  {
    id: "search",
    title: "Search band",
    description:
      "Push the search field and tags into the hero band so the pills glide directly under actionable inputs.",
  },
];

function CategoryRow({ condensed }: { condensed?: boolean }) {
  const tags = useMemo(() => CATEGORY_TAGS.slice(0, condensed ? 6 : CATEGORY_TAGS.length), [condensed]);

  return (
    <div className="flex flex-wrap gap-2">
      {tags.map((tag) => (
        <span
          key={tag}
          className="rounded-full bg-white px-3 py-1 text-xs font-semibold text-slate-600 shadow-sm shadow-slate-200"
        >
          {tag}
        </span>
      ))}
    </div>
  );
}

function PillNavigation({ compact }: { compact?: boolean }) {
  return (
    <div
      className={classnames(
        "mx-auto flex items-center justify-center gap-3 rounded-full bg-white px-4 py-2 text-sm font-semibold text-slate-600 shadow-lg shadow-slate-200",
        { "w-full max-w-3xl": !compact, "w-full max-w-xl": compact }
      )}
    >
      <span className="flex items-center gap-2 rounded-full bg-gradient-to-r from-[#2a42ff] to-[#6b8cff] px-4 py-2 text-white">
        Issues
      </span>
      <span className="rounded-full px-4 py-2">Solutions</span>
      <span className="rounded-full px-4 py-2">Action Groups</span>
    </div>
  );
}

function CurrentHero() {
  return (
    <section className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div className="flex items-center gap-2 text-xs text-slate-500">
          <span className="inline-flex items-center gap-1 rounded-full bg-white px-3 py-1 font-semibold shadow-sm">
            Search by…
          </span>
          <span className="inline-flex items-center gap-1 rounded-full bg-white px-3 py-1 font-semibold shadow-sm">
            Search by location
          </span>
        </div>
        <div className="flex flex-wrap gap-2 text-xs font-semibold text-slate-700">
          <button className="rounded-full bg-black px-4 py-2 text-white">Report</button>
          <button className="rounded-full border border-slate-300 px-4 py-2">My issues</button>
        </div>
      </div>

      <CategoryRow />

      <div className="relative overflow-hidden rounded-[32px] bg-[#dbe8ff] px-6 py-12 text-center shadow-lg shadow-slate-200">
        <img
          src={heroImage}
          alt="Illustration"
          className="pointer-events-none absolute bottom-0 left-8 hidden h-60 select-none lg:block"
        />
        <div className="mx-auto max-w-3xl space-y-6">
          <p className="text-3xl font-semibold leading-snug text-[#2f2f5b]">
            Knowledge of what is possible is the beginning of <span className="font-bold">Happiness</span>
          </p>
          <button className="mx-auto flex h-[42px] w-40 items-center justify-center rounded-full bg-white text-sm font-semibold text-[#2f2f5b] shadow">
            Report issue
          </button>
        </div>
      </div>

      <PillNavigation />
    </section>
  );
}

function CompactHero() {
  return (
    <section className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div className="flex items-center gap-2 text-xs text-slate-500">
          <span className="inline-flex items-center gap-1 rounded-full bg-white px-3 py-1 font-semibold shadow-sm">
            Search by…
          </span>
          <span className="inline-flex items-center gap-1 rounded-full bg-white px-3 py-1 font-semibold shadow-sm">
            Search by location
          </span>
        </div>
        <button className="rounded-full bg-black px-4 py-2 text-xs font-semibold text-white">Report</button>
      </div>

      <CategoryRow condensed />

      <div className="grid gap-5 rounded-[32px] bg-[#dbe8ff] p-6 shadow-lg shadow-slate-200 lg:grid-cols-[1.2fr_0.8fr]">
        <div className="flex flex-col justify-center gap-4 text-left">
          <p className="text-xs font-semibold uppercase tracking-wide text-[#44518b]">
            Community insights
          </p>
          <p className="text-3xl font-semibold text-[#2f2f5b]">
            Knowledge of what is possible is the beginning of Happiness
          </p>
          <div className="flex flex-wrap gap-3 text-xs text-[#44518b]">
            <span className="rounded-full border border-[#44518b]/20 bg-white px-3 py-1">Active issues: 142</span>
            <span className="rounded-full border border-[#44518b]/20 bg-white px-3 py-1">Action groups: 36</span>
          </div>
          <button className="w-fit rounded-full bg-white px-5 py-2 text-sm font-semibold text-[#2f2f5b] shadow">
            Report issue
          </button>
        </div>
        <div className="relative overflow-hidden rounded-2xl bg-white p-6 text-left shadow">
          <img
            src={heroImage}
            alt="Illustration"
            className="pointer-events-none mx-auto h-40 select-none"
          />
          <div className="mt-6 space-y-2 text-xs text-slate-600">
            <p>• Recent reports now surface at the top of the feed.</p>
            <p>• Pill navigation fits snugly beneath this block without extra scroll.</p>
          </div>
        </div>
      </div>

      <PillNavigation compact />
    </section>
  );
}

function SearchHero() {
  return (
    <section className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">
          Discovery search band
        </p>
        <button className="rounded-full bg-black px-4 py-2 text-xs font-semibold text-white">Report</button>
      </div>

      <div className="rounded-[32px] bg-[#dbe8ff] p-6 shadow-lg shadow-slate-200">
        <div className="grid gap-6 lg:grid-cols-[1.3fr_0.7fr]">
          <div className="space-y-4">
            <h3 className="text-3xl font-semibold text-[#2f2f5b]">
              Knowledge of what is possible is the beginning of Happiness
            </h3>
            <div className="flex flex-wrap gap-3 text-xs text-slate-600">
              <span className="rounded-full border border-[#44518b]/20 bg-white px-3 py-1">Active issues: 142</span>
              <span className="rounded-full border border-[#44518b]/20 bg-white px-3 py-1">Groups in your area</span>
            </div>
            <div className="flex items-center gap-3 rounded-full border border-white/70 bg-white px-5 py-3 text-sm text-slate-500 shadow">
              <span className="inline-flex h-8 w-8 items-center justify-center rounded-full bg-[#ecf2ff] text-[#44518b]">
                🔍
              </span>
              Search for issues, solutions or groups…
            </div>
            <CategoryRow condensed />
          </div>
          <div className="relative hidden overflow-hidden rounded-2xl bg-white p-6 shadow-sm lg:flex lg:flex-col lg:items-center lg:justify-center">
            <img
              src={heroImage}
              alt="Illustration"
              className="pointer-events-none h-44 select-none"
            />
            <p className="mt-4 text-center text-xs text-slate-500">
              Search band keeps height tight; pill navigation snaps directly below.
            </p>
          </div>
        </div>
      </div>

      <PillNavigation />
    </section>
  );
}

function HeroVariation({ variant }: { variant: string }) {
  if (variant === "compact") {
    return <CompactHero />;
  }
  if (variant === "search") {
    return <SearchHero />;
  }
  return <CurrentHero />;
}

export function HeroLayoutsShowcase() {
  const [active, setActive] = useState<string>("compact");
  const selectedVariant = HERO_VARIANTS.find((variant) => variant.id === active);

  return (
    <LayoutWrp className="bg-[#f3f6fc] px-4 py-10 sm:px-8">
      <div className="mx-auto flex w-full max-w-6xl flex-col gap-12">
        <header className="space-y-4">
          <p className="text-xs font-semibold uppercase tracking-wide text-[#2a42ff]">
            Mock-up
          </p>
          <h1 className="text-3xl font-bold text-slate-900">Hero refinements using existing visuals</h1>
          <p className="max-w-3xl text-base text-slate-600">
            Each variation rearranges your live hero elements—illustration, headline, report button, search row, and category tags—so the new Issues / Solutions / Action Groups navigation feels intentional and uncluttered.
          </p>
        </header>

        <div className="flex flex-wrap gap-3">
          {HERO_VARIANTS.map((variant) => {
            const selected = variant.id === active;
            return (
              <button
                key={variant.id}
                type="button"
                onClick={() => setActive(variant.id)}
                className={classnames(
                  "rounded-full border px-4 py-2 text-sm font-semibold transition",
                  selected
                    ? "border-[#2a42ff] bg-[#2a42ff] text-white shadow"
                    : "border-slate-300 bg-white text-slate-600 hover:border-slate-400"
                )}
              >
                {variant.title}
              </button>
            );
          })}
        </div>

        {selectedVariant && (
          <p className="text-sm text-slate-500">{selectedVariant.description}</p>
        )}

        <HeroVariation variant={active} />
      </div>
    </LayoutWrp>
  );
}

export default HeroLayoutsShowcase;
